import type { ChatOnFinishCallback, UIMessage } from 'ai';
import { serverThreadHistory } from '../server-thread-history';
import {
  currentServerThreadHead,
  hasServerThreadMessage,
  loadServerThreadBranch,
  mirrorMessage,
} from '../server-threads';
import { traceHistory, type HistoryReason } from './history-trace';

type Options = {
  signedIn?: boolean;
  /** A reader of someone else's shared chat (collaboration v1): its history
   * loads, and no submitted or finished turn is ever written to it. */
  readOnly?: boolean;
  onHistoryChange?: () => void;
  isCurrentOwner?: () => boolean;
  whenSessionKnown?: Promise<boolean>;
  onLoadedMessages?: (messages: UIMessage[]) => void;
  attachmentReferences?: boolean;
  /** A user message the transport refused before sending: it never becomes durable history. */
  isUnsent?: (messageId: string) => boolean;
};

const serverMessage = (message: UIMessage) => ({
  id: message.id,
  role: message.role,
  parts: message.parts ?? [],
});

/**
 * Keep the first submitted user message durable before dispatching its model
 * request. Run-end persistence remains independent of the mounted view, so a
 * background turn still lands in the authenticated server thread after New
 * chat, route changes, or a component unmount.
 */
export function createSubmittedTurnHistory(
  threadId: string,
  {
    signedIn: signedInSession = false,
    readOnly = false,
    onHistoryChange = () => {},
    isCurrentOwner = () => true,
    whenSessionKnown,
    onLoadedMessages,
    attachmentReferences = false,
    isUnsent = () => false,
  }: Options = {},
) {
  const base = serverThreadHistory(threadId, {
    enabled: signedInSession,
    appendable: !readOnly,
    isCurrentOwner,
    whenSessionKnown,
  });
  // Every write below is the owner's; a reader's history never writes.
  const signedIn = signedInSession && !readOnly;
  let submittedMessage: { id: string; content: string } | null = null;
  let submittedParent: string | undefined;
  // The request's finish callback survives a view unmount, unlike the SDK's
  // mounted history subscription. Both paths share this writer so the normal
  // visible run sends only one assistant message to durable history.
  const finishedMessages = new Map<string, { content: string; saved: Promise<void> }>();

  function mirrorFinishedMessage(message: UIMessage, parentId?: string | null): Promise<void> {
    if (!signedIn || !isCurrentOwner()) return Promise.resolve();
    const durable = serverMessage(message);
    const content = JSON.stringify(durable);
    const previous = finishedMessages.get(message.id);
    if (previous?.content === content) return previous.saved;
    const saved = mirrorMessage(
      threadId,
      {
        ...durable,
        ...(attachmentReferences && parentId !== undefined
          ? { parentId, expectedHeadId: parentId }
          : {}),
      },
      {
        isCurrentOwner,
        // Loaded assistants already have immutable server ancestry. The SDK
        // may append them again after submittedParent advances to a new turn.
        skipMessage: attachmentReferences && hasServerThreadMessage(threadId, message.id),
      },
    ).then(() => {
      if (isCurrentOwner()) onHistoryChange();
    });
    finishedMessages.set(message.id, { content, saved });
    return saved;
  }

  const history = {
    ...base,
    withFormat: (format: any) => {
      // Loading is delegated to the server adapter. Appends are handled here
      // so the submitted-user fingerprint can avoid a duplicate POST while a
      // generated title is still in flight.
      const inner = base.withFormat!(format) as any;
      return {
        load: async () => {
          const loaded = await inner.load();
          if (isCurrentOwner())
            onLoadedMessages?.(loaded.messages.map((item: any) => item.message));
          return loaded;
        },
        append: async (item: any) => {
          if (!signedIn || !isCurrentOwner()) return;
          if (item.message.role === 'assistant') {
            await mirrorFinishedMessage(item.message, submittedParent ?? item.parentId);
            return;
          }
          // A turn refused before it was sent leaves no user row behind (this append runs after the refusal).
          if (isUnsent(item.message.id)) return;
          const message = serverMessage(item.message);
          const skipMessage =
            (attachmentReferences && hasServerThreadMessage(threadId, message.id)) ||
            JSON.stringify(message) === submittedMessage?.content;
          if (message.id === submittedMessage?.id) submittedMessage = null;
          // Even a skipped user goes through the deletion gate and can carry
          // a title that arrived after its early durable write.
          const head =
            attachmentReferences && !skipMessage
              ? await currentServerThreadHead(threadId, isCurrentOwner)
              : undefined;
          void mirrorMessage(
            threadId,
            {
              ...message,
              ...(attachmentReferences
                ? {
                    parentId: loadServerThreadBranch(threadId)?.parents.has(message.id)
                      ? loadServerThreadBranch(threadId)!.parents.get(message.id)!
                      : (head ?? null),
                    expectedHeadId: head ?? null,
                  }
                : {}),
            },
            { skipMessage, isCurrentOwner },
          ).then(() => {
            if (isCurrentOwner()) onHistoryChange();
          });
        },
      };
    },
  };

  function requireCurrentOwner(): void {
    if (!isCurrentOwner()) throw new DOMException('Session changed', 'AbortError');
  }

  async function rememberSubmittedTurn(messages: UIMessage[]) {
    requireCurrentOwner();
    if (!signedIn) return;
    const message = messages.at(-1);
    if (message?.role !== 'user') return;
    if (isUnsent(message.id)) throw new TypeError('editing_app_unavailable');
    const durable = serverMessage(message);
    const head = attachmentReferences
      ? await currentServerThreadHead(threadId, isCurrentOwner)
      : undefined;
    const parents = loadServerThreadBranch(threadId)?.parents;
    const parentId = parents?.has(durable.id) ? parents.get(durable.id)! : (head ?? null);
    // Existing originals are ID lookups on regeneration, even if their old
    // inline body exceeds today's upload limit. The server admits the exact
    // selected ancestry and never trusts the client's replayed parts.
    if (!attachmentReferences || !hasServerThreadMessage(threadId, durable.id))
      await mirrorMessage(
        threadId,
        {
          ...durable,
          ...(attachmentReferences
            ? {
                parentId,
                expectedHeadId: head ?? null,
              }
            : {}),
        },
        { required: true, isCurrentOwner },
      );
    requireCurrentOwner();
    submittedMessage = { id: durable.id, content: JSON.stringify(durable) };
    submittedParent = durable.id;
    onHistoryChange();
    requireCurrentOwner();
  }

  const rememberFinishedTurn: ChatOnFinishCallback<UIMessage> = ({
    message,
    isAbort,
    isDisconnect,
    isError,
  }) => {
    // A disconnect may still be resumed. Never label a stopped/failed partial
    // response as a completed background answer.
    traceHistory(threadId, 'finish', {
      messageId: message.id,
      role: message.role,
      isAbort: Boolean(isAbort),
      isDisconnect: Boolean(isDisconnect),
      isError: Boolean(isError),
    });
    const refused: HistoryReason | null = !signedIn
      ? 'signed_out'
      : !isCurrentOwner()
        ? 'owner_changed'
        : isAbort
          ? 'aborted'
          : isDisconnect
            ? 'disconnected'
            : isError
              ? 'errored'
              : message.role !== 'assistant'
                ? 'not_assistant'
                : null;
    if (refused) {
      traceHistory(threadId, refused, { messageId: message.id });
      return;
    }
    void mirrorFinishedMessage(message, submittedParent).catch((error) => {
      // A network failure must not turn a valid answer red; the next durable
      // server read can still recover the completed turn when available.
      traceHistory(threadId, 'mirror_error', {
        messageId: message.id,
        error: error instanceof Error ? error.name : 'unknown',
      });
    });
  };

  return { history, rememberSubmittedTurn, rememberFinishedTurn };
}
